import { type MaybeRefOrGetter, computed, toValue } from 'vue';

import type { TreeNodeType } from './BittsBaseTree.vue';
import type { TreeNodeId } from './useTreeExpansion';

function collectMatchAncestors<TMeta>(
  nodes: TreeNodeType<TMeta>[],
  term: string,
  ancestorIds: TreeNodeId[],
  expandedIds: Set<TreeNodeId>,
) {
  nodes.forEach((node) => {
    if (node.label.toLowerCase().includes(term)) {
      ancestorIds.forEach((id) => expandedIds.add(id));
    }
    if (node.children) {
      collectMatchAncestors(
        node.children,
        term,
        [...ancestorIds, node.id],
        expandedIds,
      );
    }
  });
}

function countNodes<TMeta>(nodes: TreeNodeType<TMeta>[]): number {
  return nodes.reduce(
    (sum, node) => sum + 1 + (node.children ? countNodes(node.children) : 0),
    0,
  );
}

function pruneNodes<TMeta>(
  nodes: TreeNodeType<TMeta>[],
  term: string,
  ancestorIds: TreeNodeId[],
  expandedIds: Set<TreeNodeId>,
  collectExpansion: boolean,
): TreeNodeType<TMeta>[] {
  return nodes.reduce<TreeNodeType<TMeta>[]>((kept, node) => {
    if (node.label.toLowerCase().includes(term)) {
      if (collectExpansion) {
        ancestorIds.forEach((id) => expandedIds.add(id));
        if (node.children) {
          collectMatchAncestors(
            node.children,
            term,
            [...ancestorIds, node.id],
            expandedIds,
          );
        }
      }
      kept.push({ ...node });
      return kept;
    }

    const prunedChildren = node.children
      ? pruneNodes(
          node.children,
          term,
          [...ancestorIds, node.id],
          expandedIds,
          collectExpansion,
        )
      : [];
    if (prunedChildren.length) {
      kept.push({ ...node, children: prunedChildren });
    }
    return kept;
  }, []);
}

export function findNode<TMeta>(
  nodes: TreeNodeType<TMeta>[],
  id: TreeNodeId,
): TreeNodeType<TMeta> | undefined {
  return nodes.reduce<TreeNodeType<TMeta> | undefined>((found, node) => {
    if (found) return found;
    if (node.id === id) return node;
    return node.children ? findNode(node.children, id) : undefined;
  }, undefined);
}

function setSubtreeChecked<TMeta>(
  node: TreeNodeType<TMeta>,
  checked: boolean,
): TreeNodeType<TMeta> {
  return {
    ...node,
    ...(node.isCheckable === false ? {} : { checked }),
    ...(node.children
      ? {
          children: node.children.map((child) =>
            setSubtreeChecked(child, checked),
          ),
        }
      : {}),
  };
}

export function applyCheckedChange<TMeta>(
  nodes: TreeNodeType<TMeta>[],
  changedNodeId: TreeNodeId,
  checked: boolean,
): TreeNodeType<TMeta>[] {
  return nodes.map((node) => {
    if (node.id === changedNodeId) return setSubtreeChecked(node, checked);
    if (!node.children || node.children.length === 0) return node;

    const children = applyCheckedChange(node.children, changedNodeId, checked);
    const checkable = children.filter((child) => child.isCheckable !== false);
    return {
      ...node,
      children,
      ...(node.isCheckable === false
        ? {}
        : {
            checked:
              checkable.length > 0 && checkable.every((child) => child.checked),
          }),
    };
  });
}

const MIN_SEARCH_LENGTH = 2;
const AUTO_EXPAND_LIMIT = 2000;

export function useTreeSearch<TMeta = unknown>(
  nodeData: MaybeRefOrGetter<TreeNodeType<TMeta>[]>,
  searchTerm: MaybeRefOrGetter<string>,
  {
    minLength = MIN_SEARCH_LENGTH,
    autoExpandLimit = AUTO_EXPAND_LIMIT,
  }: { minLength?: number; autoExpandLimit?: number } = {},
) {
  const normalizedTerm = computed(() => {
    const trimmed = toValue(searchTerm).trim().toLowerCase();
    return trimmed.length >= minLength ? trimmed : '';
  });

  const treeSize = computed(() => countNodes(toValue(nodeData)));

  const searchResult = computed(() => {
    const term = normalizedTerm.value;
    if (!term) {
      return { nodes: toValue(nodeData), expandedIds: [] as TreeNodeId[] };
    }
    const canAutoExpand = treeSize.value <= autoExpandLimit;
    const expandedIds = new Set<TreeNodeId>();
    const nodes = pruneNodes(
      toValue(nodeData),
      term,
      [],
      expandedIds,
      canAutoExpand,
    );
    return { nodes, expandedIds: [...expandedIds] };
  });

  const filteredNodeData = computed(() => searchResult.value.nodes);
  const matchExpandedIds = computed(() => searchResult.value.expandedIds);

  return {
    filteredNodeData,
    matchExpandedIds,
    normalizedTerm,
    findNode,
    applyCheckedChange,
  };
}
